import React, { useState, useMemo, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { CollapseAllIcon, ExpandAllIcon } from '../ui/Icons';

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  icon?: string | React.ReactNode;
  fields: string[]; // Array of field names
  columns?: number; // Grid columns (default: 2) — used when gridCols not set
  /** Tailwind grid class (e.g. 'md:grid-cols-12'). When set, overrides columns for flexible layouts. */
  gridCols?: string;
  /** Vertical alignment of fields in the section grid (default: center). */
  gridAlign?: 'start' | 'center';
  collapsible?: boolean;
  /** When true, section starts collapsed (user can expand to edit). */
  defaultCollapsed?: boolean;
  /** When true, section has an Edit/Done button; section can be disabled (read-only) or enabled (editable). */
  toggleable?: boolean;
  /** When true with toggleable, section starts disabled (fields read-only) until user clicks Edit. */
  defaultDisabled?: boolean;
  readonly?: boolean;
  type?: 'default' | 'display'; // display for read-only sections like tracking
  className?: string;
  fieldSpans?: Record<string, string>; // Tailwind col span (e.g. 'md:col-span-4', 'md:col-span-12')
  /** Optional visibility predicate based on current form data */
  visibleWhen?: (formData: Record<string, any>) => boolean;
}

export interface FormLayout {
  mode?: 'default' | 'tabs'; // Layout mode
  sections: FormSection[];
}

export type FormLayoutVariant = 'default' | 'compact' | 'minimal';

function sectionCanCollapse(section: FormSection, variant: FormLayoutVariant): boolean {
  if (section.collapsible === false) return false;
  if (section.collapsible === true) return true;
  const isCompact = variant === 'compact' || variant === 'minimal';
  return !isCompact && section.collapsible !== false;
}

function isSectionVisible(section: FormSection, formData: Record<string, any>): boolean {
  return !section.visibleWhen || section.visibleWhen(formData);
}

interface FormLayoutRendererProps {
  layout: FormLayout;
  fields: Record<string, React.ReactNode>; // Pre-rendered field components keyed by field name
  formData?: Record<string, any>;
  className?: string;
  isMobile?: boolean;
  /** 'compact' = tighter spacing, smaller headers. 'minimal' = no section titles, labels inside fields, max compact */
  variant?: FormLayoutVariant;
  /** For toggleable sections: sectionId -> true when editable, false when disabled (read-only). */
  sectionEditState?: Record<string, boolean>;
  /** Called when user clicks Edit/Done on a toggleable section. */
  onSectionEditToggle?: (sectionId: string, enabled: boolean) => void;
}

const FormLayoutRenderer: React.FC<FormLayoutRendererProps> = ({
  layout,
  fields,
  formData = {},
  className = '',
  isMobile = false,
  variant = 'default',
  sectionEditState = {},
  onSectionEditToggle,
}) => {
  const { colors } = useTheme();
  const isCompact = variant === 'compact' || variant === 'minimal';
  const isMinimal = variant === 'minimal';
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    layout.sections?.forEach((s) => {
      if (s.defaultCollapsed) initial.add(s.id);
    });
    return initial;
  });
  const [activeTab, setActiveTab] = useState<string>(layout.sections?.[0]?.id || '');

  const collapsibleSectionIds = useMemo(
    () =>
      (layout.sections ?? [])
        .filter((s) => isSectionVisible(s, formData) && sectionCanCollapse(s, variant))
        .map((s) => s.id),
    [layout.sections, formData, variant]
  );

  const expandAllSections = useCallback(() => {
    setCollapsedSections(new Set());
  }, []);

  const collapseAllSections = useCallback(() => {
    setCollapsedSections(new Set(collapsibleSectionIds));
  }, [collapsibleSectionIds]);

  // Toggle section collapse
  const toggleSection = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  // Get grid classes: use section.gridCols when provided (e.g. md:grid-cols-12), else numeric columns.
  // Compact/minimal use the same formLayout grid/fieldSpans but tighter gap (minimal = gap-2).
  const getGridClasses = (section: FormSection) => {
    const gap = isMinimal ? 'gap-2' : isCompact ? 'gap-3' : 'gap-4';
    const align = section.gridAlign === 'start' ? 'items-start' : 'items-center';
    if (isMobile && !isCompact) {
      return `grid grid-cols-1 gap-4 ${align}`;
    }
    if (section.gridCols) {
      return `grid ${gap} ${align} grid-cols-1 ${section.gridCols}`;
    }
    const columns = section.columns ?? 2;
    const gridColsMap = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
      6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
    };
    return `grid ${gap} ${align} ${gridColsMap[columns as keyof typeof gridColsMap] || gridColsMap[2]}`;
  };

  // Get field span classes
  const getFieldSpanClasses = (fieldName: string, section: FormSection) => {
    if (section.fieldSpans && section.fieldSpans[fieldName]) {
      return section.fieldSpans[fieldName];
    }
    return '';
  };

  // Render section content
  const renderSectionContent = (section: FormSection) => {
    const isCollapsed = collapsedSections.has(section.id);
    if (isCollapsed) return null;

    const gridClasses = getGridClasses(section);

    return (
      <div className={gridClasses}>
        {section.fields.map((fieldName) => {
          const fieldComponent = fields[fieldName];
          if (!fieldComponent) return null;

          const spanClasses = getFieldSpanClasses(fieldName, section);
          return (
            <div key={fieldName} className={spanClasses || ''}>
              {fieldComponent}
            </div>
          );
        })}
      </div>
    );
  };

  // Render display section (read-only, like tracking info)
  const renderDisplaySection = (section: FormSection) => {
    if (section.visibleWhen && !section.visibleWhen(formData)) {
      return null;
    }
    return (
      <div
        key={section.id}
        className={cn(
          `${colors.surfaceHover} border rounded-lg shadow-sm`,
          section.className
        )}
      >
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            {section.icon && (
              <span className="text-xl">{typeof section.icon === 'string' ? section.icon : section.icon}</span>
            )}
            <div>
              <h3 className={cn('text-lg font-semibold', colors.text)}>{section.title}</h3>
              {section.description && (
                <p className={cn('text-sm mt-1', colors.textSecondary)}>{section.description}</p>
              )}
            </div>
          </div>
          {renderSectionContent(section)}
        </div>
      </div>
    );
  };

  // Render regular section (compact: static cards unless collapsible: true; minimal: flat groups unless collapsible: true)
  const renderSection = (section: FormSection) => {
    if (section.visibleWhen && !section.visibleWhen(formData)) {
      return null;
    }

    // Minimal: flat bordered groups by default; explicit collapsible: true uses accordion UI below (same as clients).
    if (isMinimal && section.collapsible !== true) {
      if (section.type === 'display') {
        return renderDisplaySection(section);
      }
      return (
        <div
          key={section.id}
          className={cn(
            'rounded-md border py-3 px-3',
            colors.surfaceHover,
            colors.border,
            section.className
          )}
        >
          <div className={cn('flex items-center gap-2 mb-2', colors.text)}>
            {section.icon && (
              <span className="text-sm">
                {typeof section.icon === 'string' ? section.icon : section.icon}
              </span>
            )}
            <h3 className="text-sm font-semibold">{section.title}</h3>
          </div>
          {renderSectionContent(section)}
        </div>
      );
    }

    const isCollapsed = collapsedSections.has(section.id);
    // Accordion: explicit collapsible: true always shows chevron + collapse (default + compact).
    // When collapsible is omitted, keep accordion only in default variant (compact = static grouped cards).
    const canCollapse = sectionCanCollapse(section, variant);

    if (section.type === 'display') {
      return renderDisplaySection(section);
    }

    return (
      <div
        key={section.id}
        className={cn(
          'border rounded-lg shadow-sm',
          colors.surface,
          colors.border,
          isCompact && 'rounded-md',
          section.className
        )}
      >
        {/* Section Header */}
        <div
          className={cn(
            'border-b transition-colors',
            colors.border,
            canCollapse && 'cursor-pointer',
            isCompact ? 'px-3 py-2' : 'px-6 py-4'
          )}
          onMouseEnter={(e) => {
            if (canCollapse) {
              const hoverBg = colors.surfaceHover.includes('gray-100') ? '#f3f4f6' : 
                              colors.surfaceHover.includes('gray-800') ? '#1f2937' : '';
              if (hoverBg) e.currentTarget.style.backgroundColor = hoverBg;
            }
          }}
          onMouseLeave={(e) => {
            if (canCollapse) {
              e.currentTarget.style.backgroundColor = '';
            }
          }}
          onClick={canCollapse ? () => toggleSection(section.id) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className={cn('flex items-center gap-3', isCompact && 'gap-2')}>
              {section.icon && (
                <span className={cn(isCompact ? 'text-base' : 'text-lg')}>
                  {typeof section.icon === 'string' ? section.icon : section.icon}
                </span>
              )}
              <div>
                <h3 className={cn(isCompact ? 'text-sm font-semibold' : 'text-lg font-semibold', colors.text)}>{section.title}</h3>
                {section.description && !isCompact && (
                  <p className={cn('text-sm mt-1', colors.textSecondary)}>{section.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {section.toggleable && onSectionEditToggle && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const enabled = !sectionEditState[section.id];
                    onSectionEditToggle(section.id, enabled);
                  }}
                  className={cn(
                    'p-1.5 rounded transition-colors',
                    colors.textSecondary,
                    'hover:' + colors.text,
                    sectionEditState[section.id] && 'opacity-90'
                  )}
                  title={sectionEditState[section.id] ? 'Done' : 'Edit'}
                  aria-label={sectionEditState[section.id] ? 'Done' : 'Edit'}
                >
                  {sectionEditState[section.id] ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  )}
                </button>
              )}
              {canCollapse && (
                <button
                  type="button"
                  className={cn('p-1 rounded', colors.textSecondary, 'hover:' + colors.text)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(section.id);
                  }}
                >
                  <svg
                    className={cn('w-5 h-5 transition-transform', isCollapsed ? 'rotate-0' : 'rotate-180')}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Section Content */}
        <div className={isCompact ? 'px-3 py-3' : 'px-6 py-6'}>{renderSectionContent(section)}</div>
      </div>
    );
  };

  // Render tabs
  const renderTabs = () => {
    return (
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className={cn('border-b', colors.border)}>
          <nav className="flex space-x-8">
            {layout.sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveTab(section.id)}
                className={cn(
                  'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === section.id
                    ? `${colors.primary} ${colors.primaryText} border-indigo-500`
                    : `border-transparent ${colors.textMuted}`
                )}
                onMouseEnter={(e) => {
                  if (activeTab !== section.id) {
                    e.currentTarget.style.color = '';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== section.id) {
                    e.currentTarget.style.color = '';
                  }
                }}
              >
                {section.icon && (
                  <span className="mr-2">
                    {typeof section.icon === 'string' ? section.icon : section.icon}
                  </span>
                )}
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {layout.sections.map((section) => (
            <div key={section.id} className={activeTab === section.id ? 'block' : 'hidden'}>
              <div className="space-y-6">
                {section.description && (
                  <p className={cn('text-sm', colors.textSecondary)}>{section.description}</p>
                )}
                {renderSectionContent(section)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!layout || !layout.sections) {
    return <div className={className}>No layout configuration provided</div>;
  }

  const showSectionToolbar = layout.mode !== 'tabs' && collapsibleSectionIds.length > 0;

  return (
    <div className={cn(isMinimal ? 'space-y-2' : isCompact ? 'space-y-3' : 'space-y-6', className)}>
      {showSectionToolbar && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={expandAllSections}
            className={cn(
              'inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors',
              colors.border,
              colors.surface,
              colors.text,
              'hover:bg-gray-50 dark:hover:bg-gray-800/60'
            )}
            title="Expand all"
            aria-label="Expand all"
          >
            <ExpandAllIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={collapseAllSections}
            className={cn(
              'inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors',
              colors.border,
              colors.surface,
              colors.text,
              'hover:bg-gray-50 dark:hover:bg-gray-800/60'
            )}
            title="Collapse all"
            aria-label="Collapse all"
          >
            <CollapseAllIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {layout.mode === 'tabs' ? renderTabs() : layout.sections.map(renderSection)}
    </div>
  );
};

export default FormLayoutRenderer;

